/**
 * Admin AI Queue Page — alle AI acties + per-category queues + versies.
 *
 * Tabs:
 *  - Approvals (managed acties die wachten)
 *  - Alle acties (filter op status)
 *  - Backlinks queue
 *  - Listings queue
 *  - Website versies (rollback overzicht)
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Crown, Zap, CheckCircle, RefreshCw, Sparkles, Link2, MapPin, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_PILL = (s) => {
  const map = {
    suggested: { l: 'Voorgesteld', c: '#64748b' },
    approved: { l: 'Goedgekeurd', c: '#16a34a' },
    executing: { l: 'Uitvoeren...', c: '#2563eb' },
    completed: { l: 'Voltooid', c: '#16a34a' },
    failed: { l: 'Mislukt', c: '#dc2626' },
    blocked: { l: 'Wacht', c: '#D4A017' },
    rejected: { l: 'Afgewezen', c: '#94a3b8' },
    rolled_back: { l: 'Teruggedraaid', c: '#94a3b8' },
  };
  return map[s] || { l: s, c: '#64748b' };
};

const Stats = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Totaal acties', value: stats.total, color: '#0f172a' },
    { label: 'Wacht op approval', value: stats.pending_approvals, color: '#D4A017' },
    ...Object.entries(stats.by_status || {}).map(([s, v]) => ({
      label: s.replace('_', ' '), value: v, color: s === 'completed' ? '#16a34a' : s === 'failed' ? '#dc2626' : '#64748b',
    })),
  ];
  return (
    <div data-testid="ai-queue-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
      {cards.map((k, i) => (
        <div key={i} style={{ background: 'white', borderRadius: 12, padding: '14px 16px', border: '1px solid #f1f5f9' }}>
          <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.value || 0}</p>
          <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginTop: 5 }}>{k.label}</p>
        </div>
      ))}
    </div>
  );
};

const ActionRow = ({ a, onApprove, onReject, busy, showActions = true }) => {
  const s = STATUS_PILL(a.status);
  return (
    <div data-testid={`queue-item-${a.id}`} style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      background: '#f8fafc', borderRadius: 10, padding: '12px 14px', gap: 12, flexWrap: 'wrap', marginBottom: 8,
    }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ background: `${s.c}15`, color: s.c, fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 5, textTransform: 'uppercase' }}>{s.l}</span>
          <span style={{ background: '#fff', border: '1px solid #e2e8f0', fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 5, color: '#64748b', textTransform: 'uppercase' }}>{a.category}</span>
          {a.execution_mode && <span style={{ background: '#fff', border: '1px solid #e2e8f0', fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 5, color: '#64748b', textTransform: 'uppercase' }}>{a.execution_mode}</span>}
        </div>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{a.title}</p>
        {a.description && (
          <p style={{ fontSize: 11, color: '#64748b', marginTop: 2, maxWidth: 600 }}>{a.description.slice(0, 200)}</p>
        )}
        <p style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
          {a.user?.email || a.user_id?.slice(0, 8)} · {a.action_type}
          {a.created_at && ` · ${new Date(a.created_at).toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
        </p>
      </div>
      {showActions && ['blocked', 'suggested'].includes(a.status) && (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            data-testid={`approve-${a.id}`}
            onClick={() => onApprove(a.id)}
            disabled={busy}
            style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
          >
            ✓ Approve
          </button>
          <button
            data-testid={`reject-${a.id}`}
            onClick={() => onReject(a.id)}
            disabled={busy}
            style={{ background: 'white', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
};

const TABS = [
  { id: 'approvals', label: 'Wacht op approval', icon: Crown, count: 'pending_approvals' },
  { id: 'all', label: 'Alle acties', icon: Sparkles },
  { id: 'blocked', label: 'Geblokkeerd', icon: Clock },
  { id: 'backlinks', label: 'Backlinks', icon: Link2 },
  { id: 'listings', label: 'Listings', icon: MapPin },
  { id: 'versions', label: 'Versies', icon: Clock },
];

export default function AiQueuePage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('approvals');
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await api('get', '/api/admin/ai/stats').catch(() => ({ data: null }));
      if (s.data) setStats(s.data);

      if (tab === 'approvals') {
        const r = await api('get', '/api/admin/ai/approvals');
        setItems(r.data.approvals || []);
      } else if (tab === 'all') {
        const r = await api('get', '/api/admin/ai/queue');
        setItems(r.data.items || []);
      } else if (tab === 'blocked') {
        // Direct filter op status=blocked (geen bucket mapping)
        const r = await api('get', '/api/admin/ai/queue?status=blocked');
        setItems(r.data.items || []);
      } else if (tab === 'backlinks') {
        const r = await api('get', '/api/admin/ai/queue?category=backlinks');
        setItems(r.data.items || []);
      } else if (tab === 'listings') {
        const r = await api('get', '/api/admin/ai/queue?category=listings');
        setItems(r.data.items || []);
      } else if (tab === 'versions') {
        const r = await api('get', '/api/admin/ai/website-versions');
        setVersions(r.data.versions || []);
      }
    } catch (e) {
      toast.error('Laden mislukt');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (id) => {
    setBusy(true);
    try { await api('post', `/api/admin/ai/actions/${id}/approve`); toast.success('Goedgekeurd'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setBusy(false);
  };
  const reject = async (id) => {
    setBusy(true);
    try { await api('post', `/api/admin/ai/actions/${id}/reject`); toast.info('Afgewezen'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setBusy(false);
  };

  const reRollback = async (id) => {
    if (!window.confirm('Markeer deze versie weer als levend (emergency override)?')) return;
    setBusy(true);
    try {
      await api('post', `/api/admin/ai/website-versions/${id}/re-rollback`);
      toast.success('Versie heractiveerd');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-ai-queue">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
            <Zap size={18} style={{ display: 'inline', color: '#D4A017', marginRight: 8, verticalAlign: 'middle' }} />
            AI Queue
          </h1>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            Beheer alle AI acties, approvals, backlinks, listings en website versies.
          </p>
        </div>
        <button onClick={load} style={{ background: '#0f172a', color: 'white', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
          <RefreshCw size={13} /> Vernieuwen
        </button>
      </div>

      <Stats stats={stats} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              style={{
                background: 'transparent', border: 'none',
                color: active ? '#0f172a' : '#94a3b8',
                fontWeight: active ? 700 : 500, fontSize: 13,
                padding: '10px 14px', cursor: 'pointer',
                borderBottom: active ? '2px solid #D4A017' : '2px solid transparent',
                marginBottom: '-1px', display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <Icon size={13} /> {t.label}
              {t.count && stats?.[t.count] > 0 && (
                <span style={{ background: '#D4A017', color: '#0f172a', fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 9 }}>
                  {stats[t.count]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid #f1f5f9', padding: '16px 18px' }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Laden...</div>
        ) : tab === 'versions' ? (
          versions.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center' }}>
              <CheckCircle size={24} style={{ color: '#16a34a', marginBottom: 8 }} />
              <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Nog geen versies</p>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Zodra AI website wijzigingen uitvoert, verschijnen die hier.</p>
            </div>
          ) : (
            <div>
              {versions.map(v => (
                <div key={v.id} data-testid={`version-row-${v.id}`} style={{ background: '#f8fafc', borderRadius: 10, padding: 12, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>{v.page_url}</p>
                    <p style={{ fontSize: 10, color: '#94a3b8' }}>
                      User: {v.user_id?.slice(0, 8)} · {new Date(v.created_at).toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      {v.rolled_back && <span style={{ color: '#dc2626', marginLeft: 8, fontWeight: 700 }}>· Teruggedraaid</span>}
                    </p>
                  </div>
                  {v.rolled_back && (
                    <button
                      data-testid={`re-rollback-${v.id}`}
                      onClick={() => reRollback(v.id)}
                      disabled={busy}
                      style={{ background: 'white', color: '#0f172a', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Re-activeer (emergency)
                    </button>
                  )}
                </div>
              ))}
            </div>
          )
        ) : items.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center' }}>
            <CheckCircle size={24} style={{ color: '#16a34a', marginBottom: 8 }} />
            <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Niets te tonen</p>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Alles is afgehandeld in deze categorie.</p>
          </div>
        ) : (
          <div>
            {items.map(a => (
              <ActionRow key={a.id} a={a} onApprove={approve} onReject={reject} busy={busy} showActions={tab === 'approvals' || tab === 'all'} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
